import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, 
  Crown,
  ScrollText,
  Sword,
  Users,
  BookOpen,
  Map,
  Dices,
  Shield,
  CheckCircle2,
  Gem,
  X
} from 'lucide-react';

import party from './assets/images/rpg_background_short.png';

import dnd from './assets/images/dnd.jpg';
import cthulhu from './assets/images/cthulhu.png'
import cthulhupulp from './assets/images/cthulhupulp.jpg'
import tormenta from './assets/images/tormenta20.jpg'
import vampire from './assets/images/vampire.png'

// --- Shared UI Components ---

const rpgSystems = [
  { 
    name: "Dungeons & Dragons", 
    edition: "5ª Edição", 
    img: dnd,
    desc: "O maior e mais clássico RPG de fantasia medieval do mundo. Assuma o papel de heróis destemidos, explore masmorras ancestrais, enfrente dragões e forje lendas em uma jornada épica onde a imaginação é o único limite."
  },
  { 
    name: "Call of Cthulhu", 
    edition: "7ª Edição", 
    img: cthulhu,
    desc: "Mistério e horror cósmico na elegante e sombria década de 1920. Torne-se um investigador lidando com cultos obscuros e verdades enlouquecedoras que a humanidade jamais deveria descobrir. Você conseguirá manter sua sanidade?"
  },
  { 
    name: "Vampiro: A Máscara", 
    edition: "5ª Edição", 
    img: vampire,
    desc: "Um cenário de horror pessoal e intriga política nas noites modernas. Jogue como um vampiro lutando para manter os restos de sua humanidade enquanto navega pelas teias de poder de clãs imortais escondidos nas sombras da sociedade."
  },
  { 
    name: "Cthulhu Pulp", 
    edition: "7ª Edição", 
    img: cthulhupulp,
    desc: "Toda a atmosfera do horror cósmico, mas com o dobro de ação! Inspirado nas revistas pulp dos anos 30, aqui seus investigadores são heróis durões e exploradores prontos para enfrentar os monstros com os punhos cerrados e armas em punho."
  },
  { 
    name: "Tormenta 20", 
    edition: "Jogo do Ano", 
    img: tormenta,
    desc: "O maior RPG de fantasia do Brasil! Um universo vibrante repleto de deuses, magia letal e ameaças colossais. Reúna sua guilda no mundo de Arton e lute contra a terrível tempestade rubra em campanhas repletas de heroísmo e glória."
  }
];

const SectionSeparator = () => (
  <div className="flex justify-center items-center py-16 opacity-40">
    <div className="h-[1px] w-24 bg-gradient-to-r from-transparent to-[#971253]"></div>
    <div className="mx-6 text-[#971253]">
      <Crown size={20} strokeWidth={1} />
    </div>
    <div className="h-[1px] w-24 bg-gradient-to-l from-transparent to-[#971253]"></div>
  </div>
);

// --- Main Page Component ---

const RpgExperiencePage = () => {
  const navigate = useNavigate();
  const [selectedSystem, setSelectedSystem] = useState(null);

  // Lógica inteligente de roteamento! 
  // O 'extraData' permite passar o sistema escolhido no modal direto para o formulário.
  const handleReservationClick = (type, extraData = {}) => {
    navigate("/reserva", {
      state: {
        prefillData: { 
          reservationType: type, 
          numPeople: 4, // Padrão de mesa
          ...extraData 
        }
      }
    });
  };

  return (
    <div className="min-h-screen bg-[#FFF5EE] text-stone-800 font-sans selection:bg-[#971253] selection:text-white overflow-x-hidden">

      {/* 1. HERO SECTION */}
      <section className="relative h-screen flex flex-col items-center justify-center px-6 text-center bg-[#528a89] overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute top-0 left-0 w-64 h-64 border-r border-b border-[#FFF5EE]/20 rounded-br-[100px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-64 h-64 border-l border-t border-[#FFF5EE]/20 rounded-tl-[100px] pointer-events-none" />
        
        {/* Animated Content */}
        <div className="max-w-4xl z-10 animate-in fade-in slide-in-from-bottom-8 duration-1000">
          <span className="flex items-center justify-center gap-2 text-[#FFF5EE] uppercase tracking-[0.2em] text-sm font-medium mb-8">
            <ScrollText size={16} /> Experiência Imersiva
          </span>
          
          <h1 className="font-serif text-6xl md:text-8xl text-[#FFF5EE] leading-[1.1] mb-10 [text-shadow:4px_4px_0px_#234e4d]">
            Role. Jogue. <br/> 
            <span className="italic text-5xl md:text-7xl opacity-90 block mt-2">
              Crie Lendas.
            </span>
          </h1>
          
          <p className="max-w-lg mx-auto text-lg text-[#FFF5EE] leading-relaxed mb-12 font-light drop-shadow-md">
            A verdadeira magia acontece ao redor da mesa. Uma experiência narrativa profunda com a elegância, o conforto e a gastronomia do nosso bistrô.
          </p>

          <button onClick={() => handleReservationClick('rpg')} className="group relative px-10 py-5 bg-[#971253] text-[#FFF5EE] rounded-full overflow-hidden shadow-[0_10px_20px_-5px_rgba(0,0,0,0.3)] hover:shadow-xl hover:shadow-[#971253]/40 transition-all border border-[#FFF5EE]/30">
            <span className="relative z-10 flex items-center font-medium tracking-wide text-lg">
              Reservar Mesa de RPG <Sword className="ml-3 w-5 h-5 group-hover:rotate-12 transition-transform duration-500" />
            </span>
            <div className="absolute inset-0 bg-[#7a0e42] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300 ease-out" />
          </button>
        </div>

        {/* Bottom Wave */}
        <div className="absolute bottom-0 left-0 right-0">
           <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="w-full h-auto block align-middle">
              <path fill="#FFF5EE" fillOpacity="1" d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,250.7C960,235,1056,181,1152,165.3C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
           </svg>
        </div>
      </section>

      {/* 2. EDITORIAL INTRODUCTION */}
      <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16 items-center">
          
          {/* Image Area */}
          <div className="md:col-span-5 relative">
            <div className="aspect-[4/5] rounded-tl-[100px] rounded-br-[100px] overflow-hidden shadow-2xl bg-stone-200 relative">
               <img 
                 src={party} 
                 alt="Grupo jogando RPG na Deli da Persy" 
                 className="object-cover w-full h-full hover:scale-105 transition-transform duration-1000"
               />
               <div className="absolute inset-0 bg-gradient-to-t from-[#971253]/30 to-transparent mix-blend-multiply" />
            </div>
            <div className="absolute -z-10 top-6 -left-6 w-full h-full border border-[#528a89]/60 rounded-tl-[100px] rounded-br-[100px]" />
          </div>

          {/* Text Content */}
          <div className="md:col-span-7 space-y-8">
            <span className="text-[#528a89] font-medium tracking-widest uppercase text-sm flex items-center gap-2">
               <Sparkles size={16}/> A Magia do Analógico
            </span>
            <h2 className="font-serif text-3xl md:text-5xl text-[#971253] leading-tight">Mais do que um jogo, <h2 className=" italic font-serif text-3xl md:text-5xl text-[#971253] leading-tight"> uma Imersão</h2></h2>
            <div className="prose prose-lg text-stone-600">
              <p>
                O RPG de mesa é a forma suprema de narrativa compartilhada. Na Deli da Persy, transformamos essa experiência criando o cenário perfeito para que sua guilda se preocupe apenas com a história.
              </p>
              <p>
                Oferecemos um ambiente acusticamente pensado, mesas espaçosas projetadas para acomodar mapas e fichas, e um acervo de materiais premium — desde miniaturas detalhadas até terrenos 3D. Tudo isso acompanhado do conforto e da gastronomia do nosso bistrô.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. HOW TO PLAY (MAIN SECTION) */}
      <section className="py-24 px-6 bg-white border-y border-[#971253]/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl text-[#971253] mb-4">Como Jogar na Deli</h2>
            <p className="text-stone-500 text-lg">Escolha o formato ideal para a sua jornada.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* OPTION 1: DELI DUNGEON MASTER (Premium) */}
            <div className="relative group p-10 bg-[#971253]/5 border border-[#971253]/20 rounded-[2rem] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 overflow-hidden flex flex-col">
              <div className="absolute top-0 right-0 w-48 h-48 bg-[#971253]/5 rounded-full blur-3xl pointer-events-none" />
              
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#971253]/10 text-[#971253] mb-6">
                <Crown size={32} strokeWidth={1.5} />
              </div>
              
              <h3 className="font-serif text-3xl text-[#971253] mb-2">Sessão com Mestre da Casa</h3>
              <p className="text-stone-600 mb-8 flex-grow">A experiência definitiva. Nossos Dungeon Masters profissionais conduzem sua história, preparando cenários épicos e imersivos para você e seus amigos.</p>
              
              <div className="bg-white/60 p-6 rounded-2xl mb-8 border border-[#971253]/10">
                <div className="flex items-baseline gap-1 mb-4 text-[#971253]">
                  <span className="text-lg font-medium">R$</span>
                  <span className="text-5xl font-serif">222</span>
                  <span className="text-sm font-medium text-stone-500">/ mesa</span>
                </div>
                
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-stone-600"><CheckCircle2 size={18} className="text-[#971253]/70" /> <span>Duração de 2 a 3 horas</span></li>
                  <li className="flex items-center gap-3 text-stone-600"><CheckCircle2 size={18} className="text-[#971253]/70" /> <span>Grupos de até 6 jogadores</span></li>
                  <li className="flex items-center gap-3 text-stone-600"><CheckCircle2 size={18} className="text-[#971253]/70" /> <span>Incluso livros, dados e miniaturas</span></li>
                  <li className="flex items-center gap-3 text-stone-600"><CheckCircle2 size={18} className="text-[#971253]/70" /> <span>Requer reserva (1 semana antes)</span></li>
                </ul>
              </div>

              <button onClick={() => handleReservationClick('rpg')} className="w-full py-4 bg-[#971253] text-[#FFF5EE] rounded-xl font-medium tracking-wide shadow-md hover:bg-[#7a0e42] transition-colors">
                Reservar Mesa Guiada
              </button>
            </div>

            {/* OPTION 2: PLAY WITH YOUR OWN DM (Casual) */}
            <div className="relative group p-10 bg-white border border-[#528a89]/20 rounded-[2rem] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 overflow-hidden flex flex-col">
              
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#528a89]/10 text-[#528a89] mb-6">
                <Users size={32} strokeWidth={1.5} />
              </div>
              
              <h3 className="font-serif text-3xl text-[#528a89] mb-2">Traga sua Própria Guilda</h3>
              <p className="text-stone-600 mb-8 flex-grow">O espaço perfeito para o seu grupo regular. Venha com seu próprio mestre, use nossas mesas preparadas e aproveite o ambiente.</p>
              
              <div className="bg-[#FFF5EE]/50 p-6 rounded-2xl mb-8 border border-[#528a89]/10">
                <div className="flex items-baseline gap-1 mb-4 text-[#528a89]">
                  <span className="text-lg font-medium">R$</span>
                  <span className="text-5xl font-serif">29</span>
                  <span className="text-sm font-medium text-stone-500">/ pessoa (Passaporte)</span>
                </div>
                
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-stone-600"><CheckCircle2 size={18} className="text-[#528a89]/70" /> <span>Mesas ideais para mapas de batalha</span></li>
                  <li className="flex items-center gap-3 text-stone-600"><CheckCircle2 size={18} className="text-[#528a89]/70" /> <span>Acesso aos dados e livros da casa</span></li>
                  <li className="flex items-center gap-3 text-stone-500 text-sm mt-2 italic"><span className="opacity-0">--</span> *Sujeito à disponibilidade do salão</li>
                </ul>
              </div>

              {/* Redirecionado para a reserva de Boardgame padrão se o DM for próprio */}
              <button onClick={() => handleReservationClick('boardgame')} className="w-full py-4 bg-transparent border-2 border-[#528a89] text-[#528a89] rounded-xl font-medium tracking-wide hover:bg-[#528a89] hover:text-[#FFF5EE] transition-colors">
                Venha Jogar
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* 4. AVAILABLE SYSTEMS (VISUAL HIGHLIGHT) */}
      <section className="py-24 px-6 bg-[#FFF5EE]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl text-[#971253] mb-4">Sistemas Disponíveis</h2>
            <p className="text-stone-500 text-lg">Os mundos e regras prontos para serem explorados nas nossas sessões narradas.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {rpgSystems.map((sys, idx) => (
              <div key={idx} onClick={() => setSelectedSystem(sys)} className="group relative bg-white p-4 rounded-2xl shadow-sm border border-[#971253]/10 hover:shadow-xl hover:-translate-y-2 transition-all duration-500 cursor-pointer flex flex-col items-center text-center">
                <div className="w-full aspect-[3/4] rounded-lg overflow-hidden mb-4 relative bg-stone-100">
                  <img src={sys.img} alt={sys.name} className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end justify-center pb-4">
                    <span className="text-white text-sm font-medium flex items-center gap-1"><BookOpen size={14}/> Ver Mais</span>
                  </div>
                </div>
                <h4 className="font-serif text-lg text-[#971253] leading-tight mb-1">{sys.name}</h4>
                <span className="text-xs text-stone-400 font-medium uppercase tracking-wider">{sys.edition}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. ACCESSORIES & MATERIALS */}
      <section className="py-20 px-6 bg-white border-t border-[#971253]/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Dices, title: "Dados Especiais", desc: "Sets completos para todos os sistemas, disponíveis para empréstimo nas mesas." },
              { icon: Gem, title: "Miniaturas", desc: "Vasta coleção de miniaturas pintadas para heróis, monstros e NPCs." },
              { icon: Map, title: "Terrenos e Mapas", desc: "Grids de batalha, tiles 3D e cenários modulares para combates táticos." },
              { icon: BookOpen, title: "Biblioteca", desc: "Acesso a manuais do jogador, guias de monstros e suplementos de regras." }
            ].map((item, idx) => (
              <div key={idx} className="p-8 rounded-2xl bg-[#FFF5EE]/50 border border-[#528a89]/10 text-center hover:bg-[#FFF5EE] transition-colors">
                <item.icon size={36} strokeWidth={1.5} className="text-[#528a89] mx-auto mb-4" />
                <h4 className="font-serif text-xl text-[#528a89] mb-2">{item.title}</h4>
                <p className="text-sm text-stone-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionSeparator />

      {/* 6. FINAL CTA */}
      <section className="py-20 px-6 text-center relative overflow-hidden">
        <div className="max-w-2xl mx-auto relative z-10">
          <Shield className="mx-auto w-12 h-12 text-[#971253] mb-6 opacity-80" />
          <h2 className="font-serif text-5xl md:text-7xl text-[#971253] mb-8">Pronto para começar sua aventura?</h2>
          <p className="text-stone-600 text-lg mb-10">
            Reúna sua equipe. A mesa principal está limpa, os dados estão rolando e o bistrô está aguardando seus pedidos.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button onClick={() => handleReservationClick('rpg')} className="w-full sm:w-auto px-10 py-4 bg-[#971253] text-[#FFF5EE] rounded-xl text-lg font-medium tracking-wide hover:bg-[#7a0e42] hover:-translate-y-1 transition-all shadow-lg hover:shadow-[#971253]/30">
              Reservar Mesa de RPG
            </button>
            <button onClick={() => navigate('/sobre/reserva')} className="w-full sm:w-auto px-10 py-4 bg-transparent border-2 border-[#528a89] text-[#528a89] rounded-xl text-lg font-medium tracking-wide hover:bg-[#528a89] hover:text-[#FFF5EE] transition-all">
              Ver Outras Reservas
            </button>
          </div>
        </div>
        
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-[#971253]/5 rounded-full z-0 pointer-events-none" />
      </section>

      {/* --- MODAL DO SISTEMA DE RPG --- */}
      {selectedSystem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
          
          <div className="bg-[#FFF5EE] rounded-3xl max-w-md w-full relative overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
            
            <button 
              onClick={() => setSelectedSystem(null)}
              className="absolute top-4 right-4 z-10 p-2 bg-white/50 backdrop-blur-md rounded-full text-stone-800 hover:bg-[#971253] hover:text-white transition-colors"
            >
              <X size={20} />
            </button>

            <div className="w-full h-48 relative">
              <img src={selectedSystem.img} alt={selectedSystem.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#FFF5EE] to-transparent" />
            </div>

            <div className="p-8 pt-0 text-center relative z-10 -mt-6">
              <span className="inline-block px-3 py-1 bg-[#528a89]/10 text-[#528a89] rounded-full text-xs font-bold uppercase tracking-widest mb-3">
                {selectedSystem.edition}
              </span>
              <h3 className="font-serif text-3xl text-[#971253] mb-4">{selectedSystem.name}</h3>
              <p className="text-stone-600 mb-8 leading-relaxed">
                {selectedSystem.desc}
              </p>

              <button 
                onClick={() => {
                  setSelectedSystem(null);
                  // Roteia para o formulário informando que é RPG
                  // *Poderíamos passar selectedRpgSystem: selectedSystem.name se o hook de reserva for atualizado para ler isso!
                  handleReservationClick('rpg', { selectedRpgSystem: selectedSystem.name }); 
                }}
                className="w-full py-4 bg-[#528a89] text-white rounded-xl font-medium tracking-wide hover:bg-[#3f6d6c] transition-colors"
              >
                Quero jogar este sistema
              </button>
            </div>
            
          </div>
          
          <div className="absolute inset-0 -z-10" onClick={() => setSelectedSystem(null)}></div>
        </div>
      )}

    </div>
  );
};

export default RpgExperiencePage;